import { ExternalLink, MessageCircle, ShieldCheck, Star, UserRoundCheck } from "lucide-react";
import { GOOGLE_PROFILE_URL, images } from "@/data/marvinContent";

export const AboutGalleryReviews = ({ t }) => (
  <>
    <section data-testid="about-section" className="py-16 sm:py-20">
      <div className="section-shell grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
        <div className="text-left">
          <p data-testid="about-label" className="text-sm font-extrabold uppercase tracking-[0.22em] text-amber-600">{t.about.label}</p>
          <h2 data-testid="about-title" className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#0A2540] sm:text-4xl lg:text-5xl">{t.about.title}</h2>
          <p data-testid="about-description" className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">{t.about.text}</p>
          <div data-testid="about-points" className="mt-6 grid gap-3">
            {t.about.points.map((point, index) => (
              <div key={point} data-testid={`about-point-${index}`} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <UserRoundCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
                <span className="font-bold text-slate-800">{point}</span>
              </div>
            ))}
          </div>
        </div>
        <aside data-testid="about-trust-card" className="soft-card rounded-[2rem] border border-slate-200 bg-white p-6 text-left sm:p-8">
          <div data-testid="about-trust-icon" className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-[#0A2540]">
            <ShieldCheck className="h-7 w-7" aria-hidden="true" />
          </div>
          <p data-testid="about-trust-heading" className="font-heading text-2xl font-extrabold tracking-tight text-[#0A2540]">{t.about.promiseTitle}</p>
          <p data-testid="about-trust-copy" className="mt-3 text-base leading-relaxed text-slate-600">{t.about.promiseText}</p>
          <div data-testid="about-trust-stats" className="mt-6 grid grid-cols-2 gap-3">
            <div data-testid="about-trust-stat-years" className="rounded-2xl bg-blue-50 p-4">
              <p className="font-heading text-3xl font-extrabold text-[#0A2540]">20+</p>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-slate-500">{t.about.yearsLabel}</p>
            </div>
            <div data-testid="about-trust-stat-family" className="rounded-2xl bg-amber-50 p-4">
              <p className="font-heading text-3xl font-extrabold text-[#0A2540]">24/7</p>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-slate-500">{t.about.emergencyLabel}</p>
            </div>
          </div>
        </aside>
      </div>
    </section>

    <section id="gallery" data-testid="gallery-section" className="bg-white py-16 sm:py-20">
      <div className="section-shell">
        <div className="mb-8 max-w-3xl text-left">
          <p data-testid="gallery-label" className="text-sm font-extrabold uppercase tracking-[0.22em] text-amber-600">{t.gallery.label}</p>
          <h2 data-testid="gallery-title" className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#0A2540] sm:text-4xl lg:text-5xl">{t.gallery.title}</h2>
          <p data-testid="gallery-description" className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{t.gallery.text}</p>
        </div>
        <div data-testid="gallery-grid" className="mobile-gallery-scroll -mx-4 flex gap-5 overflow-x-auto px-4 pb-4 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0">
          {t.gallery.jobs.map((job, index) => (
            <article key={job.title} data-testid={`gallery-job-${index}`} className="min-w-[88vw] rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 shadow-sm sm:min-w-0">
              <h3 data-testid={`gallery-job-title-${index}`} className="mb-3 px-2 font-heading text-xl font-extrabold text-[#0A2540]">{job.title}</h3>
              <div className="grid gap-3 lg:grid-cols-2">
                <img data-testid={`gallery-before-image-${index}`} src={images[job.before]} alt={`${job.alt} - before`} className="w-full rounded-2xl bg-white object-contain" />
                <img data-testid={`gallery-after-image-${index}`} src={images[job.after]} alt={`${job.alt} - after`} className="w-full rounded-2xl bg-white object-contain" />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="reviews" data-testid="reviews-section" className="py-16 sm:py-20">
      <div className="section-shell">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div className="max-w-3xl text-left">
            <p data-testid="reviews-label" className="text-sm font-extrabold uppercase tracking-[0.22em] text-amber-600">{t.reviews.label}</p>
            <h2 data-testid="reviews-title" className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#0A2540] sm:text-4xl lg:text-5xl">{t.reviews.title}</h2>
            <p data-testid="reviews-description" className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{t.reviews.text}</p>
          </div>
          <a data-testid="google-profile-link" href={GOOGLE_PROFILE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 text-sm font-extrabold text-[#0A2540] shadow-sm transition-transform hover:-translate-y-0.5 hover:border-amber-300">
            {t.reviews.link}<ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
        <div data-testid="reviews-grid" className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {t.reviews.cards.map((review, index) => (
            <article key={`${review.name}-${index}`} data-testid={`review-card-${index}`} className="rounded-[1.5rem] border border-slate-200 bg-white p-5 text-left shadow-sm">
              <div data-testid={`review-stars-${index}`} className="mb-4 flex text-amber-400" aria-label="five stars">
                {[0, 1, 2, 3, 4].map((star) => <Star key={star} className="h-4 w-4 fill-amber-400" aria-hidden="true" />)}
              </div>
              <p data-testid={`review-text-${index}`} className="text-sm leading-6 text-slate-600">“{review.text}”</p>
              <p data-testid={`review-name-${index}`} className="mt-5 text-sm font-extrabold text-[#0A2540]">{review.name}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="service-area-section" className="pb-16 sm:pb-20">
      <div className="section-shell rounded-[2rem] border border-slate-200 bg-white p-6 text-left shadow-sm sm:p-8">
        <p data-testid="service-area-label" className="text-sm font-extrabold uppercase tracking-[0.22em] text-amber-600">{t.area.label}</p>
        <h2 data-testid="service-area-title" className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#0A2540] sm:text-4xl">{t.area.title}</h2>
        <p data-testid="service-area-description" className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">{t.area.text}</p>
        <div data-testid="service-area-cities" className="mt-6 flex flex-wrap gap-2">
          {t.area.cities.map((city, index) => <span key={city} data-testid={`service-area-city-${index}`} className="rounded-full bg-blue-50 px-4 py-2 text-sm font-extrabold text-[#0A2540]">{city}</span>)}
        </div>
      </div>
    </section>
  </>
);